import { useQuery } from "convex/react";
import { Monitor } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { type LocationById, printerGetAllByLocationId } from "~/api-refs";

import { MultiCombobox } from "../ui/multi-combobox";
import { TerminalLocationSelector } from "./terminal-location-selector";

type PrinterGroup = "Printers";

type DisplayListGroup = Record<
  PrinterGroup,
  {
    value: string;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }[]
>;

interface LocationSettingsBlockProps {
  location: LocationById;
}

export const LocationSettingsBlock = ({
  location,
}: LocationSettingsBlockProps) => {
  const data = useQuery(printerGetAllByLocationId, {
    locationId: location._id,
  });

  const listValues = useMemo((): DisplayListGroup => {
    const object: DisplayListGroup = { Printers: [] };
    if (data) {
      object.Printers = data.map((printer) => ({
        value: printer._id,
        label: printer.name,
        icon: Monitor,
      }));
    }
    return object;
  }, [data]);

  const groupLabels = useMemo(() => {
    return data ? { Printers: "Printers" } : {};
  }, [data]);

  const [printersDisplaysValues, setPrintersDisplaysValues] = useState<
    Record<PrinterGroup, string[]>
  >({
    Printers: [],
  });

  useEffect(() => {
    if (data && printersDisplaysValues.Printers.length === 0) {
      setPrintersDisplaysValues({
        Printers: data.map((printer) => printer._id),
      });
    }
  }, [data, printersDisplaysValues.Printers.length]);

  return (
    <div>
      <div className="space-y-1 py-5">
        <p className="text-foreground text-weight-medium">Location Settings</p>
        <p className="body-sm text-muted-foreground leading-tight">
          Manage location level settings
        </p>
      </div>

      <div className="space-y-2">
        <p className="label text-foreground leading-tight">
          Default Printers for items
        </p>
        <MultiCombobox
          groups={data ? (["Printers"] as const) : []}
          disabled={!data}
          groupLabels={groupLabels}
          items={listValues}
          noResultsLabel="No devices found"
          onValueChange={(value) => {
            setPrintersDisplaysValues(value);
          }}
          values={printersDisplaysValues}
        />
      </div>

      <TerminalLocationSelector location={location} />
    </div>
  );
};
